// UI to look over all API endpoints, do some filtering, etc.
import {type PartialOptions, UI} from "../../../stem-core/ui/UIBase";
import type {NodeAttributes} from "../../../stem-core/ui/NodeAttributes";
import {SortableTable} from "../../../stem-core/ui/table/SortableTable.jsx";
import {Table, TableRow} from "../../../stem-core/ui/table/Table.jsx";
import {Modal, type ModalOptions} from "../../../stem-core/ui/modal/Modal.jsx";
import {Select, TextInput} from "../../../stem-core/ui/input/Input.jsx";
import {fetch} from "../../../stem-core/base/Fetch.js";
import {Label} from "../../../stem-core/ui/SimpleElements";
import {Level, Size} from "../../../stem-core/ui/Constants.js";

const CENTER_COLUMN_STYLE = {
    textAlign: "center",
    margin: "auto",
    verticalAlign: "middle",
};

interface EndpointThrottle {
    scope: string;
    rates: {count: number; duration: number}[];
}

class APIEndpointEntry {
    // Set by Object.assign below - `declare` so no initializer is emitted.
    declare throttle: EndpointThrottle;
    declare path: string;
    declare method: string;
    declare permissions?: string;
    declare deprecated?: boolean;
    declare deprecation_message?: string;
    declare schema?: Record<string, any>;
    declare examples?: any[];
    // Read by APIEndpointDetailsModal.render(), but the payload only ever sets `throttle`,
    // so it renders empty. Declared rather than fixed - this migration is types-only.
    declare throttle_rates?: string;

    constructor(rawObj: Record<string, any>) {
        Object.assign(this, rawObj);
    }

    getThrottleStr() {
        let str = "";
        for (const rate of this.throttle.rates) {
            if (str.length) {
                str += ",";
            }
            str += rate.count + "/" + rate.duration + "s";
        }
        return this.throttle.scope + " (" + str + ")";
    }

    getThrottleValue() {
        const rate = this.throttle.rates[0];
        return rate.count / rate.duration;
    }
}

const TABLE_COLUMNS = [{
    headerName: "Endpoint",
    rawValue: (entry: APIEndpointEntry) => entry.path,
    value: (entry: APIEndpointEntry) => {
        let result = [<code>{entry.path}</code>];
        if (entry.deprecated) {
            result = [
                <Label level={Level.WARNING} domTitle={entry.deprecation_message}>Deprecated</Label>,
                ...result
            ]
        }
        return result;
    },
    headerStyle: {verticalAlign: "middle"},
}, {
    headerName: "Method",
    rawValue: (entry: APIEndpointEntry) => entry.method,
    value: (entry: APIEndpointEntry) => <code>{entry.method}</code>,
    headerStyle: {verticalAlign: "middle"},
    cellStyle: CENTER_COLUMN_STYLE,
}, {
    headerName: "Permissions",
    value: (entry: APIEndpointEntry) => entry.permissions,
    headerStyle: {verticalAlign: "middle"},
    cellStyle: CENTER_COLUMN_STYLE,
}, {
    headerName: "Throttles",
    rawValue: (entry: APIEndpointEntry) => entry.getThrottleValue(), // TODO: calculate
    value: (entry: APIEndpointEntry) => <code>{entry.getThrottleStr()}</code>,
    headerStyle: {verticalAlign: "middle"},
    cellStyle: CENTER_COLUMN_STYLE,
}, {
    headerName: "Num examples",
    value: (entry: APIEndpointEntry) => entry.examples.length,
    headerStyle: {verticalAlign: "middle"},
    cellStyle: CENTER_COLUMN_STYLE,
}];

// One row of an endpoint's schema, as blink_endpoints_with_examples.json lists it: a key with its
// type, plus whatever constraints the endpoint declared for it.
interface SchemaEntry {
    key: string;
    type?: string;
    required?: boolean;
}

const SCHEMA_TABLE_COLUMNS = [{
    headerName: "Key",
    value: (entry: SchemaEntry) => entry.key,
    headerStyle: {verticalAlign: "middle"},
}, {
    headerName: "Type",
    value: (entry: SchemaEntry) => {
        const {type} = entry;
        if (!type) {
            return <code>Err: No type</code>;
        }

        let result = type;

        for (const [key, value] of Object.entries(entry)) {
            if (key != "key" && key != "type" && key != "required") {
                result += " " + key + "=" + JSON.stringify(value);
            }
        }

        return <code>{result}</code>;
    },
    headerStyle: {verticalAlign: "middle", textTransform: "capitalize"},
}, {
    headerName: "Required",
    value: (entry: SchemaEntry) => entry.required ? "Yes" : "No",
    headerStyle: {verticalAlign: "middle"},
    cellStyle: CENTER_COLUMN_STYLE,
}]

interface APIEndpointDetailsModalOptions extends ModalOptions {
    entry?: APIEndpointEntry;
}

export class APIEndpointDetailsModal extends Modal<APIEndpointDetailsModalOptions> {
    getInterface() {
        const {entry} = this.options;
        const {schema} = entry;

        let keys = Object.entries(schema || {});
        keys = keys.map((arr) => {
            return {
                ...arr[1],
                key: arr[0]
            };
        });

        return [
            keys.length ? [<h2>Interface:</h2>, <Table columns={SCHEMA_TABLE_COLUMNS} entries={keys} />] : <h2>No arguments</h2>,
        ]
    }

    getDeprecationMessage() {
        const {deprecated, deprecation_message} = this.options.entry;
        if (!deprecated) {
            return null;
        }
        return <div>
            <Label level={Level.WARNING} size={Size.LARGE}>Deprecated</Label>
            <span style={{fontSize: 16, padding: "0.5em"}}>{deprecation_message}</span>
        </div>
    }

    getExamples() {
        const {examples} = this.options.entry;
        if (!examples || examples.length === 0) {
            return <h2>No examples available</h2>
        }

        let result = [
            <h2>{examples.length} example calls</h2>
        ];

        const formatJSON = (obj: unknown) => {
            const str = JSON.stringify(obj, null, 3);
            return <pre>{str}</pre>;
        }

        for (let index = 0; index < examples.length; index += 1) {
            const example = examples[index];
            result.push(<h1>Example {index}</h1>,
                <strong>Request body</strong>,
                formatJSON(example.body),
                <strong>Response (status code {example.status_code})</strong>,
                formatJSON(example.response),
                <strong>Headers:</strong>,
                formatJSON(example.headers),
                )
        }
        return result;
    }

    render() {
        const {entry} = this.options;
        return [
            <h1><code>{entry.path}</code></h1>,
            <strong>Throttle: {entry.throttle_rates}</strong>,
            this.getDeprecationMessage(),
            this.getInterface(),
            this.getExamples(),
        ]
    }
}

export class APIEndpointSummaryRow extends TableRow {
    showModal() {
        // Just the entry: the modal reads nothing else off its options, and spreading the row's whole
        // bag handed it the row index and the element's own members as well
        APIEndpointDetailsModal.show({
            entry: this.options.entry,
            fillScreen: true,
        });
    }

    onMount() {
        super.onMount();
        this.addClickListener(() => this.showModal());
    }
}

export class APIEndpointsSummaryTable extends SortableTable<APIEndpointEntry> {
    getDefaultOptions(): PartialOptions<APIEndpointsSummaryTable> {
        return {
            entries: [],
            columns: TABLE_COLUMNS,
        }
    }

    getRowClass() {
        return APIEndpointSummaryRow;
    }
}

interface APIEndpointsFilterOptions {
    entries?: APIEndpointEntry[];
}

export class APIEndpointsFilter extends UI.Primitive("span")<APIEndpointsFilterOptions> {
    declare rightsSelect: Select<string>;
    declare methodSelect: Select<string>;
    declare findInput: TextInput;
    // Only set once a filter runs; getFilteredEntries() falls back to the unfiltered list.
    declare filteredEntries?: APIEndpointEntry[];

    render() {
        let methodOptionsSet = new Set();
        for (const entry of this.getEntries()) {
            methodOptionsSet.add(entry.method);
        }

        const updateFilter = () => this.updateFilter();

        return [<Select ref="rightsSelect" options={["All", "Admin", "Merchant", "Authenticated", "Any"]}
                        style={{height: "2.2em", margin: 10}}
                        onChange={updateFilter}/>,
            <Select ref="methodSelect" options={["All", ...Array.from(methodOptionsSet)]}
                        style={{height: "2.2em", margin: 10}}
                        onChange={updateFilter}/>,
            <TextInput ref="findInput" style={{height: "2.03em", margin: 10, verticalAlign: "middle"}}
                       placeholder="Search in endpoints..."
                       onChange={updateFilter} />,
            <strong>{this.getFilteredEntries().length} / {this.getEntries().length} entries</strong>

        ];
    }

    filterEntry(entry: APIEndpointEntry) {
        function matchesFilter(str: string, filter: string) {
            if (filter != "All" && filter != "") {
                return String(str).indexOf(filter) != -1;
            }
            return true;
        }

        return matchesFilter(entry.permissions, this.rightsSelect.getValue()) &&
               matchesFilter(entry.method, this.methodSelect.getValue()) &&
               matchesFilter(entry.path, this.findInput.getValue());
    }

    updateFilter() {
        this.filteredEntries = this.getEntries().filter((entry) => this.filterEntry(entry));
        this.redraw();
        this.dispatch("change");
    }

    setEntries(entries: APIEndpointEntry[]) {
        this.updateOptions({entries});
        this.dispatch("change");
    }

    getEntries() {
        return this.options.entries;
    }

    getFilteredEntries() {
        return this.filteredEntries || this.getEntries();
    }

    onMount() {
        // TODO: should have an onChange for TextInput
        this.findInput.addNodeListener("keyup", () => this.updateFilter());
    }
}

interface APIEndpointsSummaryOptions {
    url?: string;
    entries?: APIEndpointEntry[];
    headers?: Record<string, string>;
}

export class APIEndpointsSummary extends UI.Element<APIEndpointsSummaryOptions> {
    declare filter: APIEndpointsFilter;
    declare table: APIEndpointsSummaryTable;

    getDefaultOptions(): PartialOptions<APIEndpointsSummary> {
        return {
            url: "/resources/blink_endpoints_with_examples.json",
        }
    }

    extraNodeAttributes(attr: NodeAttributes) {
        attr.setStyle({
            margin: "1em",
        });
    }

    getEntries() {
        return this.options.entries || [];
    }

    render() {
        const entries = this.getEntries();
        return [
            <APIEndpointsFilter ref="filter" entries={entries}/>,
            <APIEndpointsSummaryTable ref="table" entries={this.getFilteredEntries()} />
        ]
    }

    getFilteredEntries() {
        return this.filter?.getFilteredEntries();
    }

    async updateData(url: string) {
        let entries = await fetch(url, {
            method: "GET",
            dataType: "json",
            headers: this.options.headers || {},
        });
        entries = entries.map((entry: Record<string, any>) => new APIEndpointEntry(entry));
        this.updateOptions({entries});
        this.filter?.updateFilter();
    }

    onMount() {
        this.updateData(this.options.url);
        this.filter.addListener("change", () => {
            const entries = this.filter.getFilteredEntries();
            this.table.setEntries(entries);
        });
    }
}
